import { AttachmentReturn } from '../../clients/rust-api-client';

/**
 * What the file service accepts. Mirrors `ALLOWED_EXTENSIONS` in
 * `back-end-rs/src/api/v1/file/models.rs`.
 */
export const ALLOWED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'heic',
  'heif',
] as const;

/** The `accept` attribute. A filter for the picker, never a guarantee. */
export const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');

/** Mirrors `MAX_FILE_SIZE`; the server answers 413 past it. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/**
 * What a browser will actually paint in an `<img>`.
 *
 * Deliberately narrower than `AttachmentReturn.renderable`, which means
 * "the inline endpoint will serve this" and is true of PDFs. Deciding the
 * render from `renderable` alone puts a PDF in an `<img>` tag.
 */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
};

/**
 * Should this attachment be fetched and shown as a picture?
 *
 * `renderable === false` is the heic/heif case: stored faithfully,
 * downloads fine, and only Safari paints it. It is not damage.
 */
export const isImageAttachment = (attachment: AttachmentReturn): boolean =>
  attachment.renderable &&
  IMAGE_EXTENSIONS.indexOf(attachment.file_extension.toLowerCase()) !== -1;

export interface FileRejection {
  file: File;
  reason: string;
}

/**
 * Check a picked file before anything goes on the wire.
 *
 * The server remains the authority — this is not a security boundary. It
 * exists so that a 40 MB video is refused in the dialog instead of being
 * pushed up a phone tether to be told no.
 */
export const rejectionFor = (file: File): string | null => {
  const extension = extensionOf(file.name);

  if (!extension) {
    return 'That file has no extension, so we cannot tell what it is.';
  }
  if (ALLOWED_EXTENSIONS.indexOf(extension as never) === -1) {
    return `.${extension} files cannot be attached.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `That file is ${formatFileSize(file.size)}. The limit is 25 MB.`;
  }
  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
